import { ErrorCode, Purchases, PurchasesError } from "@revenuecat/purchases-js";
import type { CustomerInfo, EntitlementInfo } from "@revenuecat/purchases-js";

// La API key pública determina la app de RevenueCat (p. ej. app24b98ffed5 → rcb_sb_...).
// El SDK web no recibe un appId: para cambiar de app, cambia la key.
const REVENUECAT_API_KEY = import.meta.env.VITE_REVENUECAT_API_KEY;

// Offering a mostrar. Si no se define, se usa el marcado como "current" en el dashboard.
export const REVENUECAT_OFFERING_ID: string | undefined = import.meta.env.VITE_REVENUECAT_OFFERING_ID;

export const PRO_ENTITLEMENT_ID = "vendeplay_pro";

export interface MonthlyPlan {
  packageId: string;
  entitlementId: string;
  name: string;
  features: string[];
}

// Planes mensuales en orden ascendente. Se buscan por identificador de paquete:
// offering.monthly solo devuelve $rc_monthly. El precio sale siempre del producto.
export const MONTHLY_PLANS: MonthlyPlan[] = [
  { packageId: "$rc_monthly", entitlementId: "basic", name: "Básico", features: [] },
  { packageId: "monthly_plus", entitlementId: "plus", name: "Plus", features: [] },
  { packageId: "monthly_pro", entitlementId: PRO_ENTITLEMENT_ID, name: "Pro", features: [] },
];

/**
 * Configura el SDK (una sola instancia por app) e identifica al usuario.
 * El appUserId debe ser estable: usamos el id de la cuenta para que la
 * suscripción pertenezca a la cuenta y no a un navegador concreto.
 */
export async function initRevenueCat(appUserId: string): Promise<Purchases> {
  if (!REVENUECAT_API_KEY) {
    throw new Error("Falta VITE_REVENUECAT_API_KEY en el .env");
  }

  if (!Purchases.isConfigured()) {
    return Purchases.configure({ apiKey: REVENUECAT_API_KEY, appUserId });
  }

  const purchases = Purchases.getSharedInstance();
  if (purchases.getAppUserId() !== appUserId) {
    await purchases.changeUser(appUserId);
  }
  return purchases;
}

/** Plan mensual activo de mayor nivel (Pro > Plus > Básico), con su entitlement. */
export function getActivePlan(
  customerInfo: CustomerInfo | undefined,
): { plan: MonthlyPlan; entitlement: EntitlementInfo } | null {
  const active = customerInfo?.entitlements.active ?? {};
  for (const plan of [...MONTHLY_PLANS].reverse()) {
    const entitlement = active[plan.entitlementId];
    if (entitlement) return { plan, entitlement };
  }
  return null;
}

export function isUserCancelled(error: unknown): boolean {
  return error instanceof PurchasesError && error.errorCode === ErrorCode.UserCancelledError;
}

export function getRevenueCatErrorMessage(error: unknown): string {
  if (!(error instanceof PurchasesError)) {
    return "Ocurrió un error inesperado. Intenta de nuevo.";
  }

  switch (error.errorCode) {
    case ErrorCode.NetworkError:
      return "Error de red. Revisa tu conexión e intenta de nuevo.";
    case ErrorCode.ProductAlreadyPurchasedError:
      return "Ya tienes este plan activo.";
    case ErrorCode.PaymentPendingError:
      return "Tu pago está pendiente de confirmación.";
    case ErrorCode.PurchaseInvalidError:
    case ErrorCode.ProductNotAvailableForPurchaseError:
      return "Este plan no está disponible para compra en este momento.";
    case ErrorCode.InvalidEmailError:
      return "El correo electrónico no es válido.";
    case ErrorCode.InvalidCredentialsError:
    case ErrorCode.ConfigurationError:
      return "Error de configuración de pagos. Contacta a soporte.";
    default:
      return error.message || "No se pudo completar la operación.";
  }
}
